import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import {
  CAlert, CBadge, CButton, CCol, CFormInput, CFormLabel, CFormSelect,
  CModal, CModalBody, CModalFooter, CModalHeader, CModalTitle, CRow, CSpinner,
} from '@coreui/react';
import { api, problemMessage, type Schemas } from '../../api/client';
import { RichTextEditor, RichTextView } from '../../components/RichText';
import { PriorityBadge, StateBadge } from '../../components/StateBadge';
import { formatDate, priorityLabels } from '../../format';
import { ItemsSection } from './ItemsSection';
import { NextStepBar } from './OperationsPanel';
import { PracticeCoach } from '../../tour/PracticeCoach';
import { QuestionsSection } from './QuestionsSection';
import { AssignmentsPanel, AttachmentsPanel, HistorySection, NotesSection } from './SidePanels';

type Contract = Schemas['ContractResponse'];

/**
 * Detail zakázky — místo, kde se s ní skutečně pracuje.
 * <p>
 * `version` se zvedne po každé změně z kterékoli sekce a ostatní se podle ní obnoví. Nestačí
 * `rowVersion`: některé změny (přiřazení, dotaz) mění, co jde se zakázkou udělat, aniž by ho zvedly.
 */
export function ContractDetailPage() {
  const id = Number(useParams().id);
  const [contract, setContract] = useState<Contract | null>(null);
  const [version, setVersion] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  const reload = useCallback(() => {
    api.GET('/api/v1/contracts/{id}', { params: { path: { id } } }).then(({ data, error: problem, response }) => {
      if (data) {
        setContract(data);
        setVersion((v) => v + 1);
      } else {
        setError(response.status === 404 ? 'Zakázka neexistuje nebo byla smazána.' : problemMessage(problem));
      }
    });
  }, [id]);

  useEffect(reload, [reload]);

  const changed = (updated: Contract) => {
    setContract(updated);
    setVersion((v) => v + 1);
  };

  if (error) return <CAlert color="danger">{error}</CAlert>;
  if (!contract) return <CSpinner color="primary" />;

  return (
    <>
      <div className="mb-2"><Link to="/zakazky" className="small">← Zakázky</Link></div>

      <div className="d-flex justify-content-between align-items-start mb-4 gap-3">
        <div>
          <div className="text-body-secondary">{contract.contractNumber} · <span className="fw-semibold text-body">{contract.customer.name}</span></div>
          <h2 className="mb-2">{contract.title}</h2>
          {contract.description && <Description text={contract.description} contractId={contract.id} />}
          <div className="d-flex gap-2 align-items-center flex-wrap">
            <StateBadge state={contract.state} />
            <PriorityBadge priority={contract.priority} />
            {contract.isBlocked && <CBadge color="danger">blokováno</CBadge>}
            <span className={contract.isOverdue ? 'text-danger fw-semibold' : 'text-body-secondary'}>
              Termín {formatDate(contract.dueDate)}{contract.isOverdue && ' — po termínu'}
            </span>
          </div>
        </div>
        <div className="d-flex gap-2">
          <CButton color="secondary" variant="outline" as={Link} to={`/zakazky/${contract.id}/pruvodka`}>Průvodka</CButton>
          {contract.isHeaderEditable && <CButton color="primary" variant="outline" onClick={() => setEditing(true)}>Upravit</CButton>}
        </div>
      </div>

      {contract.isPractice && <PracticeCoach contract={contract} version={version} />}
      <NextStepBar contract={contract} version={version} onChanged={changed} />

      <CRow className="g-4">
        <CCol lg={8}>
          <ItemsSection contract={contract} onChanged={changed} />
          <QuestionsSection contractId={contract.id} version={version} onChanged={reload} />
          <NotesSection contractId={contract.id} onChanged={() => setVersion((v) => v + 1)} />
        </CCol>
        <CCol lg={4}>
          <AssignmentsPanel contractId={contract.id} version={version} onChanged={reload} />
          <AttachmentsPanel contractId={contract.id} version={version} />
          <HistorySection contractId={contract.id} version={version} />
        </CCol>
      </CRow>

      {editing && (
        <HeaderEditor contract={contract} onClose={() => setEditing(false)} onSaved={(c) => { setEditing(false); changed(c); }}
          onFilesAdded={() => setVersion((v) => v + 1)} />
      )}
    </>
  );
}

/**
 * Popis zakázky. Bývá to celý zkopírovaný e-mail od zákazníka — v plné délce by vytlačil
 * všechno ostatní pod okraj (zpětná vazba 8. 10. 2026). Proto jen pár řádků a zbytek na
 * kliknutí. Krátký popis se zobrazí celý a tlačítko se neukáže.
 */
function Description({ text, contractId }: { text: string; contractId: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [overflows, setOverflows] = useState(false);

  // Merit pri kazde zmene velikosti obsahu - obrazky v popisu se nactou az po vykresleni
  // a jednorazove mereni by je minulo. Kolik radku zabere, zalezi i na sirce okna.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || open) return;
    const measure = () => setOverflows(el.scrollHeight > el.clientHeight + 2);
    measure();
    const observer = new ResizeObserver(measure);
    if (el.firstElementChild) observer.observe(el.firstElementChild);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text, open]);

  return (
    <div className="mb-2" style={{ maxWidth: 900 }}>
      <div
        ref={ref}
        style={{
          overflow: 'hidden',
          maxHeight: open ? 'none' : '6em',
          // Jemne doznivani misto useknuteho radku, at je videt, ze text pokracuje.
          maskImage: !open && overflows ? 'linear-gradient(to bottom, #000 60%, transparent)' : undefined,
          WebkitMaskImage: !open && overflows ? 'linear-gradient(to bottom, #000 60%, transparent)' : undefined,
        }}
      >
        <RichTextView text={text} contractId={contractId} />
      </div>
      {(overflows || open) && (
        <CButton color="link" size="sm" className="p-0" onClick={() => setOpen(!open)}>
          {open ? 'Sbalit popis' : 'Zobrazit celý popis'}
        </CButton>
      )}
    </div>
  );
}

/** Hlavička jde měnit jen v konceptu a při kontrole — pak se podle zakázky staví. */
function HeaderEditor({ contract, onClose, onSaved, onFilesAdded }: {
  contract: Contract; onClose: () => void; onSaved: (c: Contract) => void; onFilesAdded: () => void;
}) {
  const [title, setTitle] = useState(contract.title);
  const [description, setDescription] = useState(contract.description ?? '');
  const [dueDate, setDueDate] = useState(contract.dueDate ?? '');
  const [priority, setPriority] = useState(contract.priority);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    const { data, error: problem } = await api.PATCH('/api/v1/contracts/{id}', {
      params: { path: { id: contract.id } },
      body: {
        title, description, dueDate: dueDate || null, priority,
        expectedRowVersion: contract.rowVersion,
      },
    });

    if (data) onSaved(data); else setError(problemMessage(problem));
  }

  return (
    <CModal visible onClose={onClose}>
      <CModalHeader><CModalTitle>Upravit zakázku</CModalTitle></CModalHeader>
      <CModalBody>
        <CFormLabel>Název</CFormLabel>
        <CFormInput value={title} onChange={(e) => setTitle(e.target.value)} className="mb-3" />
        <CFormLabel>Popis</CFormLabel>
        <div className="mb-3">
          <RichTextEditor value={description} onChange={setDescription} minHeight="5rem"
            contractId={contract.id} onFilesAdded={onFilesAdded} />
        </div>
        <CRow className="g-3">
          <CCol>
            <CFormLabel>Termín</CFormLabel>
            <CFormInput type="date" required value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            {/* Termin je povinny pro kontrolu - prazdne pole by se tise ignorovalo. */}
            {!dueDate && contract.dueDate && <div className="small text-danger mt-1">Termín nejde smazat, jen změnit.</div>}
          </CCol>
          <CCol>
            <CFormLabel>Priorita</CFormLabel>
            <CFormSelect value={priority} onChange={(e) => setPriority(e.target.value)}>
              {Object.entries(priorityLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </CFormSelect>
          </CCol>
        </CRow>
        {error && <CAlert color="danger" className="mt-3 mb-0">{error}</CAlert>}
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" variant="ghost" onClick={onClose}>Zpět</CButton>
        <CButton color="primary" disabled={!title.trim() || (!dueDate && !!contract.dueDate)} onClick={() => void save()}>Uložit</CButton>
      </CModalFooter>
    </CModal>
  );
}
